import React, { useEffect, useState } from 'react';

import ToastCheckIcon from '../../assets/icons/toast/checkIconToast.svg';

export interface ToastProps {
  id: string | number;
  title: string;
  message?: string;
  onClose: (id: string | number) => void;
  duration?: number;
  type?: 'success' | 'error' | string;
  isActive?: boolean;
}

const Toast = ({ id, title, message, onClose, duration = 8000, type = 'success', isActive = false }: ToastProps) => {
  const [isExiting, setIsExiting] = useState(false);

  const isError = type === 'error';

  const handleClose = () => {
    setIsExiting(true);
    // Wait for exit animation to complete before removing from DOM
    setTimeout(() => {
      onClose(id);
    }, 300);
  };

  useEffect(() => {
    // Only start the countdown once this toast is the front (active) card.
    // This prevents background toasts burning their timer while hidden behind newer ones.
    if (!isActive) return;

    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleClose is stable per render; isActive/duration are the real triggers
  }, [isActive, duration]);

  return (
    <div
      className={`
        toast-item relative overflow-hidden
        bg-white rounded-[6px] shadow-[0px_10px_30px_0px_rgba(12,17,29,0.14)]
        flex items-center gap-3
        transition-all duration-300 ease-in-out
        w-full h-[84px]
        ${isExiting
          ? 'translate-x-[120%] opacity-0'
          : 'translate-x-0 opacity-100'
        }
      `}
      style={{
        paddingTop: '24px',
        paddingRight: '56px',
        paddingBottom: '24px',
        paddingLeft: '24px',
      }}
    >
      {/* Left accent bar — Success: #117E43 | Error: #DB0000 */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-[8px] ${
          isError ? 'bg-[#DB0000]' : 'bg-[#117E43]'
        }`}
        aria-hidden="true"
      />
      {/* Icon - Success Checkmark or Error X */}
      <div
        className="flex-shrink-0 flex items-center justify-center rounded-[6px]"
        style={{
          width: 36,
          height: 36,
          background: isError ? '#FEE2E2' : '#E8FCF1',
        }}
      >
        {isError ? (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-red-600"
          >
            <circle
              cx="12"
              cy="12"
              r="11"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M8 8L16 16M16 8L8 16"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <img src={ToastCheckIcon} alt="Success" width="20" height="20" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h4 className="font-['Noto_Sans'] text-[14px] font-semibold leading-[24px] text-[#030407] m-0 truncate">
          {title}
        </h4>
        {message && (
          <p className="font-['Noto_Sans'] text-[14px] font-normal leading-[24px] text-[#667085] m-0 mt-1 line-clamp-1">
            {message}
          </p>
        )}
      </div>

      {/* Close Button */}
      <button
        onClick={handleClose}
        className="flex w-[20px] h-[20px] items-center justify-center flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors absolute top-[24px] right-[24px]"
        aria-label="Close notification"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 4L4 12M4 4L12 12"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
};

export default Toast;
